// The performance guard: frame-time distribution, GPU tiering, and the
// dev/capture readout the perf gate is judged on.
//
// WHY A DISTRIBUTION AND NOT AN AVERAGE
//
// The precedent project's own post-mortem is that a median frame time
// hides stalls — it shipped a build reporting 94 fps median that was
// actually hitching to 12–17 fps whenever a new material reached the
// rasterizer for the first time, because a smoothed average is exactly
// the statistic a periodic 60 ms compile disappears into. So the gauge
// keeps the last several seconds of raw frame times and reports p50/p95/
// p99/max and a stall count. A build passes the gate when p95 is inside
// the 16.6 ms budget, not when the EMA looks nice.
//
// The tier is the other half: the web client targets 60 fps on integrated
// graphics, and the only honest way to hold that on hardware that cannot
// afford the full stack is to spend less on it. `detectTier` reads the
// unmasked GL renderer string for the known-slow families, and
// `TierGovernor` demotes at runtime when the measured p95 says the guess
// was optimistic. It never promotes: a stack that oscillates between two
// looks is worse than one that settles on the cheaper one.

import * as THREE from "three";

/** How much history the gauge keeps (frames). ~5 s at 120 fps. */
const RING = 600;
/** A frame slower than this is a stall, not a slow frame. */
const STALL_MS = 20;

export interface FrameStats {
  /** Smoothed frames-per-second (EMA) — the historical gate number. */
  fps: number;
  /** Median frame time, ms. */
  p50: number;
  p95: number;
  p99: number;
  /** Worst frame in the window, ms. */
  max: number;
  /** Frames over STALL_MS in the window. */
  stalls: number;
  /** Frames in the window. */
  samples: number;
  /**
   * The frame the set was BUILT in, ms — reported, never averaged.
   *
   * Entering an area constructs every procedural texture and mesh in it
   * and then compiles its shaders, which is one 1–3 second frame behind
   * the wipe. Folding that into the same statistics as play frames does
   * not make the client honest, it makes it unreadable: a single 2200 ms
   * sample drags an EMA over a hundred following frames and moves a
   * vista's reported fps by three or four. It belongs on its own line —
   * it is a load-time number, and it is the number to watch if loading
   * gets slow.
   */
  buildMs: number;
}

/**
 * A ring of raw frame times with percentile queries. Push once per frame;
 * the sort only happens when someone asks (the dev handle, the capture
 * harness), never in the loop.
 */
export class FrameGauge {
  private readonly ring = new Float32Array(RING);
  private readonly scratch = new Float32Array(RING);
  private count = 0;
  private head = 0;
  private ema = 0;
  private buildMs = 0;
  /** Frames to swallow after a reset (the set's construction frame). */
  private skip = 0;

  push(ms: number): void {
    if (!(ms > 0)) return;
    if (this.skip > 0) {
      this.skip--;
      this.buildMs = Math.max(this.buildMs, round2(ms));
      return;
    }
    this.ring[this.head] = ms;
    this.head = (this.head + 1) % RING;
    if (this.count < RING) this.count++;
    const inst = 1000 / ms;
    this.ema = this.ema === 0 ? inst : this.ema * 0.95 + inst * 0.05;
  }

  /**
   * Drop the history — called when the set changes under the camera.
   *
   * The next frame is the one that BUILDS the new set, and it is a load
   * event, not a play frame: it is recorded as `buildMs` and kept out of
   * the distribution. Nothing is hidden by that — an unreported stall is
   * hidden, a stall on its own line is measured.
   */
  reset(): void {
    this.count = 0;
    this.head = 0;
    this.ema = 0;
    this.buildMs = 0;
    this.skip = 1;
  }

  stats(): FrameStats {
    const n = this.count;
    if (n === 0) {
      return { fps: 0, p50: 0, p95: 0, p99: 0, max: 0, stalls: 0, samples: 0, buildMs: 0 };
    }
    const s = this.scratch.subarray(0, n);
    s.set(this.ring.subarray(0, n));
    s.sort();
    let stalls = 0;
    for (let i = n - 1; i >= 0 && (s[i] ?? 0) > STALL_MS; i--) stalls++;
    const at = (q: number): number => round2(s[Math.min(n - 1, Math.floor(q * n))] ?? 0);
    return {
      fps: Math.round(this.ema * 10) / 10,
      p50: at(0.5),
      p95: at(0.95),
      p99: at(0.99),
      max: round2(s[n - 1] ?? 0),
      stalls,
      samples: n,
      buildMs: this.buildMs,
    };
  }
}

function round2(v: number): number {
  return Math.round(v * 100) / 100;
}

/**
 * Pay a set's whole first-sight cost inside the frame that builds it.
 *
 * Two things are lazy in a WebGL renderer and both of them are stalls the
 * player sees as a hitch several seconds into a scene, long after the
 * loading cover has gone:
 *
 *   PROGRAMS. `renderer.compile` walks the graph and links every material
 *   against the lights actually in the scene. Without it the first frame
 *   that brings a new material into view links it mid-flight.
 *
 *   TEXTURES. Compile does NOT upload images. A 2048² canvas texture is
 *   ~16 MB of upload and format conversion on first bind, and this project
 *   forges dozens of them per set; the wave-10 capture logs show exactly
 *   that shape — a 196 ms frame arriving 40 frames after a scene loaded,
 *   with no other explanation. `initTexture` moves each one into the
 *   build frame, where the wipe is already covering.
 *
 * Both are best-effort: a texture that cannot be uploaded yet is simply
 * uploaded later, as it would have been anyway.
 */
/**
 * Clamp the set's shadow maps to what this tier can afford.
 *
 * One-way, and it runs at scene build, before the map is allocated: the
 * sun rigs author the resolution their look needs (the day sun's 4096 over
 * a ±118 m box is a measured fix for stair-stepped roof lips) and a
 * machine that can pay for it keeps it. A machine that cannot gets half
 * the edge — a quarter of the shadow-pass fill — rather than a slideshow.
 */
export function clampShadowMaps(scene: THREE.Scene, tier: QualityTier): void {
  const limit = TIERS[tier].shadowMapSize;
  scene.traverse((object) => {
    const light = object as THREE.DirectionalLight;
    if (!light.isDirectionalLight || !light.castShadow) return;
    const size = light.shadow.mapSize;
    if (size.x > limit) size.x = limit;
    if (size.y > limit) size.y = limit;
  });
}

export function warmScene(
  renderer: THREE.WebGLRenderer,
  scene: THREE.Scene,
  camera: THREE.Camera,
): void {
  renderer.compile(scene, camera);
  const seen = new Set<THREE.Texture>();
  const slots = [
    "map",
    "normalMap",
    "roughnessMap",
    "metalnessMap",
    "emissiveMap",
    "alphaMap",
    "aoMap",
    "bumpMap",
    "displacementMap",
    "envMap",
    "lightMap",
  ] as const;
  const warmMaterial = (material: THREE.Material): void => {
    for (const slot of slots) {
      const texture = (material as unknown as Record<string, unknown>)[slot];
      if (texture instanceof THREE.Texture && !seen.has(texture)) {
        seen.add(texture);
        try {
          renderer.initTexture(texture);
        } catch {
          // A texture whose image is not decoded yet uploads on first use.
        }
      }
    }
  };
  scene.traverse((object) => {
    const mesh = object as THREE.Mesh;
    if (!mesh.isMesh && !(object as THREE.Sprite).isSprite) return;
    const material = (mesh as { material?: THREE.Material | THREE.Material[] }).material;
    if (!material) return;
    if (Array.isArray(material)) for (const m of material) warmMaterial(m);
    else warmMaterial(material);
  });
  if (scene.environment) {
    try {
      renderer.initTexture(scene.environment);
    } catch {
      // As above.
    }
  }
}

// ---------------------------------------------------------------------------
// Tiering

export type QualityTier = "high" | "medium" | "low";

/** Per-tier cost knobs. The post stack and the renderer both read these. */
export interface TierSettings {
  /** MSAA samples on the HDR scene buffer (0 = none). */
  samples: number;
  /** Bloom pyramid depth. Fewer levels = a tighter, cheaper halo. */
  bloomLevels: number;
  /** Ambient-occlusion resolution divisor (2 = half res); 0 = no AO. */
  aoDiv: number;
  /** Directions × steps in the AO estimator. */
  aoDirs: number;
  aoSteps: number;
  /** Five-tap best-fit normals (true) or free derivative normals. */
  aoBestFitNormal: boolean;
  /** Upper bound on devicePixelRatio. */
  maxPixelRatio: number;
  /**
   * Sun shadow-map edge, px. The scene rigs author their own (the day sun
   * runs 4096 over a ±118 m box — a measured fix for stair-stepped roof
   * lips), and the guard only ever clamps DOWNWARD from it.
   */
  shadowMapSize: number;
}

export const TIERS: Record<QualityTier, TierSettings> = {
  high: {
    samples: 4,
    bloomLevels: 6,
    aoDiv: 2,
    aoDirs: 4,
    aoSteps: 3,
    aoBestFitNormal: true,
    maxPixelRatio: 2,
    shadowMapSize: 4096,
  },
  medium: {
    samples: 2,
    bloomLevels: 5,
    aoDiv: 2,
    aoDirs: 3,
    aoSteps: 3,
    aoBestFitNormal: false,
    maxPixelRatio: 1.5,
    shadowMapSize: 2048,
  },
  low: {
    samples: 0,
    bloomLevels: 4,
    aoDiv: 4,
    aoDirs: 0,
    aoSteps: 0,
    aoBestFitNormal: false,
    maxPixelRatio: 1,
    shadowMapSize: 1024,
  },
};

/**
 * First guess from the GL renderer string. Integrated Intel parts and the
 * software rasterizers are the ones that cannot afford the full stack;
 * Apple silicon and discrete parts get the full stack and are demoted by
 * measurement if that was wrong.
 */
export function detectTier(renderer: THREE.WebGLRenderer): QualityTier {
  let name = "";
  try {
    const gl = renderer.getContext();
    const dbg = gl.getExtension("WEBGL_debug_renderer_info");
    name = String(
      (dbg && gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL)) || gl.getParameter(gl.RENDERER) || "",
    ).toLowerCase();
  } catch {
    return "medium";
  }
  if (!name) return "medium";
  if (/swiftshader|llvmpipe|software|mesa offscreen/.test(name)) return "low";
  // Intel integrated: HD/UHD Graphics are the slow half, Iris Xe the fast.
  if (/intel/.test(name)) return /iris xe|arc/.test(name) ? "medium" : "low";
  if (/mali-[gt]?[0-6]|adreno \(tm\) [1-5]|powervr/.test(name)) return "low";
  return "high";
}

/**
 * Demotes the tier when the measured p95 misses the budget for a sustained
 * window. One-way, and it waits for a full window after every change so a
 * scene swap's compile burst can't cascade a demotion.
 */
export class TierGovernor {
  private windowFrames = 0;
  private overBudget = 0;

  constructor(
    private tier: QualityTier,
    /** Frame-time budget, ms (16.6 = 60 fps). */
    private readonly budgetMs = 16.6,
    /** Frames per decision window. */
    private readonly windowSize = 240,
  ) {}

  current(): QualityTier {
    return this.tier;
  }

  /** Feed one frame; returns the new tier when it just changed. */
  sample(ms: number): QualityTier | undefined {
    this.windowFrames++;
    if (ms > this.budgetMs) this.overBudget++;
    if (this.windowFrames < this.windowSize) return undefined;
    // p95-equivalent: more than 5% of the window missed the budget.
    const missed = this.overBudget / this.windowFrames;
    this.windowFrames = 0;
    this.overBudget = 0;
    if (missed <= 0.05) return undefined;
    const next = this.tier === "high" ? "medium" : this.tier === "medium" ? "low" : undefined;
    if (!next) return undefined;
    this.tier = next;
    return next;
  }

  /** After a scene swap: the next window starts clean. */
  reset(): void {
    this.windowFrames = 0;
    this.overBudget = 0;
  }
}
